import React, { useEffect, useState, useCallback } from "react";
import { zodiacsApi } from "../../services/api";
import api from "../../services/api";
import { Card, PageHeader, Modal, Input, Button } from "../../components/ui";
import { showToast } from "../../utils/toast";
import {
  Globe,
  Globe2,
  ChevronDown,
  Check,
  Languages,
  Pencil,
  Star,
  Image,
  RotateCcw,
  X,
} from "lucide-react";


// ── Language code → flag emoji ────────────────────────────────────────────────
const CODE_TO_FLAG = {
  hi: "🇮🇳", ta: "🇮🇳", te: "🇮🇳", kn: "🇮🇳", ml: "🇮🇳",
  mr: "🇮🇳", gu: "🇮🇳", pa: "🇮🇳", bn: "🇧🇩", ur: "🇵🇰",
  en: "🇬🇧", zh: "🇨🇳", ar: "🇸🇦", fr: "🇫🇷", es: "🇪🇸",
  de: "🇩🇪", pt: "🇵🇹", ru: "🇷🇺", ja: "🇯🇵", ko: "🇰🇷",
};

function langCodeToFlag(code) {
  if (!code) return null;
  return CODE_TO_FLAG[code.toLowerCase()] || null;
}


// ── Inline Toggle ─────────────────────────────────────────────────────────────
function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: value ? "#0F3D3E" : "#e2e8f0",
        border: `1px solid ${value ? "#0a2d2e" : "#cbd5e1"}`,
        position: "relative", cursor: "pointer", flexShrink: 0,
        transition: "background 0.25s, border-color 0.25s",
      }}
    >
      <div style={{
        position: "absolute", top: 3,
        left: value ? 22 : 3,
        width: 16, height: 16, borderRadius: "50%",
        background: value ? "#ffffff" : "#94a3b8",
        transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }} />
    </div>
  );
}


// ── Language Dropdown ─────────────────────────────────────────────────────────
function LanguageDropdown({ languages, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const current = languages.find(l => l.id === selected);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  return (
    <div style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 14px", borderRadius: 8,
          border: "1px solid var(--border)", background: "var(--bg-card)",
          cursor: "pointer", fontSize: 13, color: "var(--text-primary)",
          fontWeight: 500, transition: "border-color 0.18s",
          minWidth: 160,
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = "#0F3D3E"}
        onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
      >
        {current?.flag
          ? <span style={{ fontSize: 16, lineHeight: 1 }}>{current.flag}</span>
          : <Globe size={16} strokeWidth={1.8} color="var(--text-muted)" />
        }
        <span style={{ flex: 1, textAlign: "left" }}>{current?.name || "English"}</span>
        <ChevronDown
          size={12}
          strokeWidth={1.8}
          color="var(--text-muted)"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
        />
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0,
          background: "var(--bg-card)", border: "1px solid var(--border)",
          borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          zIndex: 100, minWidth: 180, overflow: "hidden",
        }}>
          {/* English (default) */}
          <button
            onClick={() => { onChange(null); setOpen(false); }}
            style={{
              width: "100%", padding: "9px 14px", border: "none",
              background: selected === null ? "rgba(15,61,62,0.08)" : "none",
              cursor: "pointer", fontSize: 13, textAlign: "left",
              display: "flex", alignItems: "center", gap: 8,
              color: "var(--text-primary)", transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(15,61,62,0.06)"}
            onMouseLeave={e => e.currentTarget.style.background = selected === null ? "rgba(15,61,62,0.08)" : "none"}
          >
            <Globe size={16} strokeWidth={1.8} color="var(--text-muted)" />
            <span>English</span>
            {selected === null && <Check size={12} strokeWidth={2.5} color="#0F3D3E" style={{ marginLeft: "auto" }} />}
          </button>

          {languages.map(lang => (
            <button
              key={lang.id}
              onClick={() => { onChange(lang.id); setOpen(false); }}
              style={{
                width: "100%", padding: "9px 14px", border: "none",
                background: selected === lang.id ? "rgba(15,61,62,0.08)" : "none",
                cursor: "pointer", fontSize: 13, textAlign: "left",
                display: "flex", alignItems: "center", gap: 8,
                color: "var(--text-primary)", transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(15,61,62,0.06)"}
              onMouseLeave={e => e.currentTarget.style.background = selected === lang.id ? "rgba(15,61,62,0.08)" : "none"}
            >
              {lang.flag
                ? <span style={{ fontSize: 16, lineHeight: 1 }}>{lang.flag}</span>
                : <Globe size={16} strokeWidth={1.8} color="var(--text-muted)" />
              }
              <span>{lang.name}</span>
              {selected === lang.id && <Check size={12} strokeWidth={2.5} color="#0F3D3E" style={{ marginLeft: "auto" }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Translation Edit Modal ────────────────────────────────────────────────────
function TranslationModal({ zodiac, languages, open, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!zodiac || !open) return;
    const existing = zodiac.translations || [];
    const mapped = languages.map(lang => {
      const found = existing.find(t => t.languageId === lang.id);
      return { languageId: lang.id, name: found?.name || "", langName: lang.name, flag: lang.flag };
    });
    setRows(mapped);
  }, [zodiac, languages, open]);

  const handleSave = async (row) => {
    if (!row.name.trim()) { showToast("Name cannot be empty", "error"); return; }
    setSaving(row.languageId);
    try {
      await zodiacsApi.upsertTranslation(zodiac.id, {
        languageId: row.languageId,
        name: row.name.trim(),
      });
      showToast(`Translation saved for ${row.langName}`);
      onSaved?.();
    } catch (err) {
      showToast(err.response?.data?.message || "Error saving translation", "error");
    } finally {
      setSaving(null);
    }
  };

  if (!zodiac) return null;

  return (
    <Modal open={open} onClose={onClose} title={`Translations — ${zodiac.englishName}`} width={500}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.length === 0 && (
          <div style={{ color: "var(--text-muted)", fontSize: 13, textAlign: "center", padding: 20 }}>
            No languages configured. Add languages first.
          </div>
        )}

        {rows.map((row, i) => (
          <div key={row.languageId} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 12px", borderRadius: 8,
            background: i % 2 === 0 ? "var(--bg-input)" : "transparent",
            border: "1px solid var(--border)",
          }}>
            {/* Language label */}
            <div style={{ width: 110, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              {row.flag
                ? <span style={{ fontSize: 18, lineHeight: 1 }}>{row.flag}</span>
                : <Globe size={18} strokeWidth={1.6} color="var(--text-muted)" />
              }
              <span style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500 }}>
                {row.langName}
              </span>
            </div>

            {/* Translation input */}
            <input
              value={row.name}
              onChange={e => setRows(prev => prev.map((r, j) => j === i ? { ...r, name: e.target.value } : r))}
              onKeyDown={e => e.key === "Enter" && handleSave(row)}
              placeholder={`Name in ${row.langName}…`}
              style={{
                flex: 1, padding: "7px 10px", borderRadius: 6,
                border: "1px solid var(--border)", background: "var(--bg-card)",
                color: "var(--text-primary)", fontSize: 13,
                outline: "none", transition: "border-color 0.18s",
              }}
              onFocus={e => e.target.style.borderColor = "#0F3D3E"}
              onBlur={e => e.target.style.borderColor = "var(--border)"}
            />

            {/* Save button */}
            <button
              onClick={() => handleSave(row)}
              disabled={saving === row.languageId}
              style={{
                padding: "7px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                border: "none", background: "#0F3D3E", color: "#fff",
                cursor: saving === row.languageId ? "not-allowed" : "pointer",
                opacity: saving === row.languageId ? 0.6 : 1,
                transition: "opacity 0.18s, background 0.18s", flexShrink: 0,
              }}
              onMouseEnter={e => { if (saving !== row.languageId) e.currentTarget.style.background = "#0a2d2e"; }}
              onMouseLeave={e => e.currentTarget.style.background = "#0F3D3E"}
            >
              {saving === row.languageId ? "…" : "Save"}
            </button>
          </div>
        ))}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}


// ── Image Edit Modal ──────────────────────────────────────────────────────────
function ImageEditModal({ zodiac, open, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", iconUrl: "", symbol: "" });
  const [iconFile, setIconFile] = useState(null);
  const [symbolFile, setSymbolFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);

  useEffect(() => {
    if (!zodiac || !open) return;
    setForm({ name: zodiac.englishName || "", iconUrl: zodiac.iconUrl || "", symbol: zodiac.symbol || "" });
    setIconFile(null); setSymbolFile(null);
    setIconPreview(zodiac.iconUrl || null);
  }, [zodiac, open]);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0]; if (!file) return;
    if (type === "icon") { setIconFile(file); setIconPreview(URL.createObjectURL(file)); }
    else setSymbolFile(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      if (iconFile)    fd.append("iconFile",   iconFile);
      if (symbolFile)  fd.append("symbolFile", symbolFile);
      if (!iconFile)   fd.append("iconUrl",    form.iconUrl);
      if (!symbolFile) fd.append("symbolUrl",  form.symbol);
      await zodiacsApi.updateImages(zodiac.id, fd);
      showToast("Zodiac updated successfully");
      onClose();
      onSaved?.();
    } catch (err) {
      showToast(err.response?.data?.message || "Error updating zodiac", "error");
    }
  };

  if (!zodiac) return null;

  return (
    <Modal open={open} onClose={onClose} title="Edit Zodiac" width={480}>
      <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Name — read-only */}
        <div>
          <label style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500, display: "block", marginBottom: 6 }}>
            Name
          </label>
          <div style={{
            padding: "9px 12px", borderRadius: "var(--radius)",
            background: "var(--bg-input)", border: "1px solid var(--border)",
            fontSize: 13, display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ flex: 1, color: "var(--text-primary)", fontWeight: 500 }}>
              {zodiac.englishName || "—"}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 600, letterSpacing: "0.05em",
              padding: "2px 6px", borderRadius: 4,
              background: "var(--bg-card)", border: "1px solid var(--border)",
              color: "var(--text-muted)", textTransform: "uppercase", flexShrink: 0,
            }}>
              Read only
            </span>
          </div>
        </div>

        {/* Icon Image */}
        <div>
          <label style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500, display: "block", marginBottom: 6 }}>
            Icon Image (zodiac_logos/)
          </label>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            {iconPreview ? (
              <img src={iconPreview} alt="preview"
                style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 6, border: "1px solid var(--border)" }}
                onError={e => e.target.style.display = "none"} />
            ) : (
              <div style={{
                width: 48, height: 48, borderRadius: 6, border: "1px solid var(--border)",
                background: "var(--bg-input)", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Image size={20} strokeWidth={1.5} color="var(--text-muted)" />
              </div>
            )}
            <label style={{
              padding: "7px 14px", borderRadius: "var(--radius)",
              background: "var(--bg-input)", border: "1px solid var(--border)",
              color: "var(--text-secondary)", cursor: "pointer", fontSize: 12,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <Image size={13} strokeWidth={2} />
              {iconFile ? iconFile.name : "Choose file…"}
              <input type="file" accept="image/*" style={{ display: "none" }}
                onChange={e => handleFileChange(e, "icon")} />
            </label>
          </div>
          {!iconFile && (
            <Input label="Or paste URL" value={form.iconUrl}
              onChange={e => setForm({ ...form, iconUrl: e.target.value })}
              style={{ marginTop: 8 }} />
          )}
        </div>

        {/* Symbol Image */}
        <div>
          <label style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500, display: "block", marginBottom: 6 }}>
            Symbol Image (zodiac_icons/)
          </label>
          <label style={{
            padding: "7px 14px", borderRadius: "var(--radius)",
            background: "var(--bg-input)", border: "1px solid var(--border)",
            color: "var(--text-secondary)", cursor: "pointer", fontSize: 12,
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <Image size={13} strokeWidth={2} />
            {symbolFile ? symbolFile.name : "Choose file…"}
            <input type="file" accept="image/*" style={{ display: "none" }}
              onChange={e => handleFileChange(e, "symbol")} />
          </label>
          {!symbolFile && (
            <Input label="Or paste URL" value={form.symbol}
              onChange={e => setForm({ ...form, symbol: e.target.value })}
              style={{ marginTop: 8 }} />
          )}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
          <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit">Update</Button>
        </div>
      </form>
    </Modal>
  );
}


// ── Zodiac Card ───────────────────────────────────────────────────────────────
const SIGN_SYMBOLS = ["♈","♉","♊","♋","♌","♍","♎","♏","♐","♑","♒","♓"];

function ZodiacCard({ zodiac, index, selectedLanguage, onEditImages, onEditTranslations }) {
  const displayName = zodiac.translatedName || zodiac.englishName;
  const isTranslated = selectedLanguage && zodiac.translatedName && zodiac.translatedName !== zodiac.englishName;

  return (
    <Card>
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        {zodiac.iconUrl ? (
          <img src={zodiac.iconUrl} alt={zodiac.englishName}
            style={{ width: 64, height: 64, objectFit: "contain", margin: "0 auto 8px", display: "block", borderRadius: 8 }}
            onError={e => e.target.style.display = "none"} />
        ) : (
          <div style={{ fontSize: 40, marginBottom: 8, lineHeight: 1 }}>
            {SIGN_SYMBOLS[index % 12]}
          </div>
        )}

        <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>
          {displayName}
        </div>

        {isTranslated && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>
            {zodiac.englishName}
          </div>
        )}

        {zodiac.symbol && (
          <img src={zodiac.symbol} alt={`${zodiac.englishName} symbol`}
            style={{ height: 28, objectFit: "contain", opacity: 0.65, margin: "6px auto 0", display: "block" }}
            onError={e => e.target.style.display = "none"} />
        )}
      </div>

      {/* Card footer */}
      <div style={{
        display: "flex",
        justifyContent: selectedLanguage ? "space-between" : "center",
        alignItems: "center",
        paddingTop: 10, borderTop: "1px solid var(--border)", gap: 6,
      }}>
        {/* Edit images */}
        <Button size="sm" variant="outline" onClick={() => onEditImages(zodiac)}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <Pencil size={12} strokeWidth={2} /> Edit
          </span>
        </Button>

        {/* Translate button */}
        {selectedLanguage && (
          <button
            title="Edit translations"
            onClick={() => onEditTranslations(zodiac)}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500,
              border: `1px solid ${isTranslated ? "#0F3D3E" : "var(--border)"}`,
              background: "none",
              color: isTranslated ? "#0F3D3E" : "var(--text-muted)",
              cursor: "pointer", transition: "all 0.18s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = "#0F3D3E";
              e.currentTarget.style.color = "#0F3D3E";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = isTranslated ? "#0F3D3E" : "var(--border)";
              e.currentTarget.style.color = isTranslated ? "#0F3D3E" : "var(--text-muted)";
            }}
          >
            <Languages size={13} strokeWidth={2} />
            {isTranslated ? "Translated" : "Translate"}
          </button>
        )}
      </div>
    </Card>
  );
}


// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Zodiacs() {
  const [zodiacs,               setZodiacs]               = useState([]);
  const [loading,               setLoading]               = useState(true);
  const [selectedLang,          setSelectedLang]          = useState(null);
  const [languages,             setLanguages]             = useState([]);
  const [editImagesTarget,      setEditImagesTarget]      = useState(null);
  const [editTranslationsTarget,setEditTranslationsTarget]= useState(null);

  // ── Load languages once ───────────────────────────────────────────────────
  useEffect(() => {
    api.get("/admin/languages")
      .then((r) => {
        const raw = r.data?.data || r.data || [];
        const activeLanguages = raw.filter(lang => lang.active && lang.code?.toLowerCase() !== "en");
        setLanguages(
          activeLanguages.map(lang => ({
            ...lang,
            flag: langCodeToFlag(lang.code),
          }))
        );
      })
      .catch(() => {});
  }, []);

  // ── Load zodiacs ──────────────────────────────────────────────────────────
  const load = useCallback(() => {
    setLoading(true);
    zodiacsApi.getAll(selectedLang)
      .then(r => setZodiacs(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedLang]);

  useEffect(() => { load(); }, [load]);

  const handleLanguageChange = (langId) => {
    setSelectedLang(langId);
  };

  return (
    <div>
      <PageHeader
        title="Zodiac Master"
        subtitle="Manage zodiac signs, images, and translations"
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <LanguageDropdown
              languages={languages}
              selected={selectedLang}
              onChange={handleLanguageChange}
            />
          </div>
        }
      />

      {/* Language notice banner */}
      {selectedLang && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 16px", borderRadius: 8, marginBottom: 16,
          background: "rgba(15,61,62,0.06)", border: "1px solid rgba(15,61,62,0.15)",
          fontSize: 13, color: "#0F3D3E",
        }}>
          {languages.find(l => l.id === selectedLang)?.flag
            ? <span style={{ fontSize: 16, lineHeight: 1 }}>{languages.find(l => l.id === selectedLang).flag}</span>
            : <Globe2 size={16} strokeWidth={1.8} color="#0F3D3E" />
          }
          <span>
            Showing <strong>{languages.find(l => l.id === selectedLang)?.name}</strong> translations.
            Cards without a translation show their English name. Click <strong>Translate</strong> on any card to add one.
          </span>
          <button
            onClick={() => setSelectedLang(null)}
            style={{
              marginLeft: "auto", padding: "3px 10px", borderRadius: 5,
              border: "1px solid rgba(15,61,62,0.3)", background: "none",
              color: "#0F3D3E", fontSize: 12, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 5,
            }}
          >
            <RotateCcw size={11} strokeWidth={2.2} /> Reset to English
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Loading…</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))", gap: 14 }}>
          {zodiacs.map((z, i) => (
            <ZodiacCard
              key={z.id}
              zodiac={z}
              index={i}
              selectedLanguage={selectedLang}
              onEditImages={setEditImagesTarget}
              onEditTranslations={setEditTranslationsTarget}
            />
          ))}
          {zodiacs.length === 0 && (
            <div style={{ color: "var(--text-muted)", fontSize: 13 }}>No zodiacs found.</div>
          )}
        </div>
      )}

      <ImageEditModal
        zodiac={editImagesTarget}
        open={!!editImagesTarget}
        onClose={() => setEditImagesTarget(null)}
        onSaved={load}
      />

      <TranslationModal
        zodiac={editTranslationsTarget}
        languages={languages}
        open={!!editTranslationsTarget}
        onClose={() => setEditTranslationsTarget(null)}
        onSaved={load}
      />
    </div>
  );
}